import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common'; // Required for *ngIf, *ngFor
import { TableModule } from 'primeng/table';   // Required for p-table
import { TagModule } from 'primeng/tag';     // Required for p-tag
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner'; // For p-progressSpinner if you add it
import { MessageService, ConfirmationService } from 'primeng/api'; // Import services for messages and confirmations
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { PaymentType } from '../../../../models/user.model';
import { TranslateService } from '../../../../services/translate.service';
import { MaintainAccountService } from '../maintain-account.service';

import { Subject, takeUntil } from 'rxjs';
import { PaymentInfoComponent } from '../../create-account/steps/payment-info/payment-info.component';

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
	selector: 'app-tab-payment-info',
	standalone: true,
	imports: [
		CommonModule,
		TableModule,
		TagModule,
		TooltipModule,
		ButtonModule,
		ProgressSpinnerModule,
		FormsModule
	],
	templateUrl: './tab-payment-info.component.html',
	styleUrls: ['./tab-payment-info.component.scss'],
	providers: [DialogService, MessageService]

})
export class TabPaymentInfoComponent extends Pagination implements OnInit {
	// Using the non-null assertion operator (!) because these are @Input() and guaranteed by the parent
	@Input() paymentTypes!: any[];
	@Input() paymentTypesLoading!: boolean;
	@Input() paymentTypesError!: boolean;

	@Input() customerRecId!: number;

	@ViewChild('dt') dt!: Table;

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	constructor(public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService) {super(); }

	ngOnInit(): void {
		if (this.paymentTypes?.length > 0) {
			this.globalFilterFields = Object.keys(this.paymentTypes[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
	}

	getPaymentTypeSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	private getPaymentTypeDisplay(paymentType: PaymentType): string {
		if (paymentType.PaymentAccountNum) {
			return `${paymentType.PaymentType} (${paymentType.PaymentAccountNum})`;
		}
		if (paymentType.CreditCardNum) {
			return `${paymentType.CreditCardType || 'Credit Card'} (${paymentType.CreditCardNum})`;
		}
		if (paymentType.bank_account_number) {
			return `${paymentType.PaymentType} (${paymentType.bank_account_number})`;
		}
		return `${paymentType.PaymentType || 'Payment Type'} (RecID: ${paymentType.RecID})`;
	}

	editPaymentType(paymentType: PaymentType): void {
		this.ref = this.dialogService.open(PaymentInfoComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: false,
				customerRecId: this.customerRecId,
				nameEntry: {
					id: paymentType.RecID.toString(),
					paymentTypeRecId: paymentType.PaymentTypeRecID,
					bank_account_number: paymentType.bank_account_number,
					bank_branch: paymentType.bank_branch,
					bank_name: paymentType.bank_name,
					bank_number: paymentType.bank_number,
					bank_check_number: paymentType.BankCheckNum,
					credit_card_number: paymentType.CreditCardNum,
					creditCardTypeRecId: paymentType.CreditCardTypeRecID,
					paymentAccountNumber: paymentType.PaymentAccountNum,
					uniqueFeature: paymentType.UniqueFeature === 1,
					description: paymentType.Descr,
					isActive: paymentType.IsActive,
					isApproved: paymentType.IsApproved,
					customerRecId: paymentType.RecID
				}
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadPaymentInfoForUser();
			}
		});
	}


	/**
	 * Function to update the status of an payment info via an API call.
	 * It takes the RecID and the status as a number (1 for active, 0 for inactive).
	 * @param recId The RecID of the payment info to update.
	 * @param newStatus The new status (1 for active, 0 for inactive).
	 */
	updatePaymentTypeStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.paymentTypes?.findIndex(name => name.RecID === recId);
					if (this.paymentTypes && index !== undefined && index > -1) {
						// Correctly updating the 'IsActive' or 'IsApproved' property
						if (source) {
							this.paymentTypes[index].IsActive = newStatus;
						}
						else {
							this.paymentTypes[index].IsApproved = newStatus;
						}
						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `Payment mehtod "${this.paymentTypes[index].PaymentAccountNum}" is now ${statusText}.`,
							life: 3000
						});
					}
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}


	/**
	 * Handles the click event for the toggle status button on a payment record.
	 * Prompts for confirmation and then toggles the IsActive status (1 to 0, or 0 to 1).
	 */
	togglePaymentTypeStatus(paymentType: PaymentType, action: boolean): void {

		const newStatusBoolean = (action ? (paymentType.IsActive === 1 ? false : true) : (paymentType.IsApproved === 1 ? false : true));
		const newStatus = newStatusBoolean ? 1 : 0;
		const actionText = newStatusBoolean ?
			(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
			(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');

		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${paymentType.PaymentAccountNum}?`,
			header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
			rejectLabel: this.translateServ.dictionary['NO'] || 'No',
			accept: () => {
				// Call the API update function
				this.updatePaymentTypeStatus(action, paymentType.RecID, newStatus);
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
					detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
					life: 3000
				});
			}
		});
	}

	// Confirms and deletes a record
	confirmDelete(paymentType: PaymentType): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (ID: ${paymentType.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), paymentType.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000,
							});
							this.reloadPaymentInfoForUser();
						},
						error: (error) => {
							console.error('Failed to delete record:', error);
							this.messageService.add({
								severity: 'error',
								summary: this.translateServ.dictionary['ERROR'] || 'Error',
								detail: this.translateServ.dictionary['MAINT_DELETE_FAILED_MSG'] || 'Failed to delete record.',
								life: 5000,
							});
						}
					});
			},
			reject: () => {
				// The user clicked "Cancel", so we can just close the dialog.
			}
		});
	}


	createNewRecord(): void {
		this.ref = this.dialogService.open(PaymentInfoComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId // Pass the ID here
			}
		});

		// Subscribe to the dialog's onClose event
		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			// Check if the dialog was closed with a successful action (e.g., a "save" button click)
			// The AdditionalNamesComponent would need to return a value like 'true' upon success
			// to trigger this.
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadPaymentInfoForUser();
			}
		});
	}



	private reloadPaymentInfoForUser(): void {
		// Call the service method to get the updated data
		const index = this.maintainAccountService.getMappingIndex("paymentTypes");
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					this.paymentTypes = data;
					this.paymentTypesLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload apayment info:', error);
					this.paymentTypesError = true;
					this.paymentTypesLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_PAYMENT'] || 'Failed to load payment info.',
						life: 5000
					});
				}
			});
	}


	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}